import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Stack, Typography, Button, Chip, Tooltip,
  Dialog, DialogContent, DialogActions,
  TextField, LinearProgress, CircularProgress, IconButton, Divider, alpha,
  FormControl, InputLabel, Select, MenuItem, Autocomplete, Switch,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import GroupsIcon from '@mui/icons-material/Groups';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';

import DynamicCrudPage from '../../components/DynamicCrudPage';
import FileField from '../../components/FormField/FileField';
import DocumentAssignmentsField from '../../components/FormField/DocumentAssignmentsField';
import { useAuth, useDocumentBank, useNotification } from '../../hooks';
import { USER_ROLES } from '../../constants/permissions';
import { getDocumentFileTypeMeta } from '../../constants/tableConfigurations';
import s3Service from '../../services/s3Service';

// document_bank_versions.file_url kolonu bazen S3 KEY (yeni yuklemeler,
// private bucket'ta signed URL gerektiginden), bazen legacy tam URL icerir.
// Ikisinden de kullanilabilir bir S3 key cikaran yardimci.
const extractS3Key = (raw) => {
  if (!raw) return '';
  if (!/^https?:\/\//i.test(raw)) return raw;
  try {
    const u = new URL(raw);
    return decodeURIComponent(u.pathname.replace(/^\//, ''));
  } catch {
    return raw;
  }
};

// Kategori/Surec enum degerleri — form select icin. Etiketler dinamik gelir.
const CATEGORY_OPTIONS = [
  { value: 'TEKNIK', labelKey: 'documentBank.category.TEKNIK', fallback: 'Teknik' },
  { value: 'MALI', labelKey: 'documentBank.category.MALI', fallback: 'Mali' },
  { value: 'SATIS', labelKey: 'documentBank.category.SATIS', fallback: 'Satış' },
];
const PROCESS_OPTIONS = [
  { value: 'ONAY_SONRASI', labelKey: 'documentBank.process.ONAY_SONRASI', fallback: 'Onay sonrası' },
  { value: 'YAZIM', labelKey: 'documentBank.process.YAZIM', fallback: 'Yazım' },
  { value: 'DIGER', labelKey: 'documentBank.process.DIGER', fallback: 'Diğer' },
];

// ─── Ortak modal iskeleti ─────────────────────────────────────────────
const ModalHeader = ({ icon, title, subtitle, meta, onClose, tone = 'primary' }) => (
  <Box
    sx={{
      display: 'flex', alignItems: 'center', gap: 2,
      px: 3, py: 2.25,
      borderBottom: '1px solid', borderColor: 'divider',
    }}
  >
    <Box
      sx={{
        width: 40, height: 40, borderRadius: 1.5,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: (theme) => alpha(theme.palette[tone].main, 0.1),
        color: `${tone}.main`,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ lineHeight: 1.25 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" noWrap display="block">
          {subtitle}
        </Typography>
      )}
    </Box>
    {meta && <Box sx={{ flexShrink: 0 }}>{meta}</Box>}
    <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
      <CloseIcon fontSize="small" />
    </IconButton>
  </Box>
);

const modalPaperSx = { borderRadius: 3, overflow: 'hidden' };
const modalActionsSx = { px: 3, py: 2, gap: 1, borderTop: '1px solid', borderColor: 'divider' };
const sectionLabelSx = {
  fontSize: 11, fontWeight: 700, letterSpacing: 0.6,
  textTransform: 'uppercase', color: 'text.secondary',
};

const MANAGE_ROLES = new Set([
  USER_ROLES.ROOT,
  USER_ROLES.DIRECTOR,
  USER_ROLES.GROUP_MANAGER,
  USER_ROLES.TEAM_MANAGER,
]);

// ─── Etiket cok-secim alani ──────────────────────────────────────────
// Ayarlar sayfasindan tanimli sirket etiketleri arasindan cok secim.
// value: number[] (tag id array).
const TagSelectField = ({ tags, value, onChange, label }) => {
  const { t } = useTranslation();
  const options = tags || [];
  const selected = options.filter(tg => (value || []).includes(tg.id));
  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      value={selected}
      getOptionLabel={(o) => o.name || ''}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_e, next) => onChange((next || []).map(t => t.id))}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((opt, idx) => (
          <Chip
            {...getTagProps({ index: idx })}
            key={opt.id}
            size="small"
            label={opt.name}
            sx={{
              bgcolor: `${opt.color || '#3b82f6'}22`,
              color: opt.color || '#3b82f6',
              fontWeight: 600,
              border: `1px solid ${opt.color || '#3b82f6'}55`,
            }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || t('documentBank.fields.tags', 'Etiketler')}
          placeholder={t('documentBank.fields.tagsPlaceholder', 'Etiket seç')}
        />
      )}
    />
  );
};

// ─── Kategori/Surec/Etiket alanlarini paylasan bolme ──────────────────
const TaxonomySection = ({
  category, setCategory, process, setProcess,
  tagIds, setTagIds, tags,
}) => {
  const { t } = useTranslation();
  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <FormControl fullWidth size="small">
          <InputLabel>{t('documentBank.fields.category', 'Kategori')}</InputLabel>
          <Select
            value={category || ''}
            label={t('documentBank.fields.category', 'Kategori')}
            onChange={(e) => setCategory(e.target.value || null)}
          >
            <MenuItem value=""><em>{t('common.none', 'Yok')}</em></MenuItem>
            {CATEGORY_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>
                {t(o.labelKey, o.fallback)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel>{t('documentBank.fields.process', 'Süreç')}</InputLabel>
          <Select
            value={process || ''}
            label={t('documentBank.fields.process', 'Süreç')}
            onChange={(e) => setProcess(e.target.value || null)}
          >
            <MenuItem value=""><em>{t('common.none', 'Yok')}</em></MenuItem>
            {PROCESS_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>
                {t(o.labelKey, o.fallback)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <TagSelectField tags={tags} value={tagIds} onChange={setTagIds} />
    </Stack>
  );
};

// ─── Custom modal: Yeni Doküman ──────────────────────────────────────
const UploadModal = ({ open, onClose, onSubmit, currentUser, tags }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [category, setCategory] = useState(null);
  const [process, setProcess] = useState(null);
  const [tagIds, setTagIds] = useState([]);

  useEffect(() => {
    if (!open) return;
    setTitle(''); setDescription(''); setNotes('');
    setFile(null); setAssignments([]);
    setCategory(null); setProcess(null); setTagIds([]);
    setSubmitting(false);
  }, [open]);

  const canSubmit = !!file && title.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await onSubmit({
        title: title.trim(), description, file, notes, assignments,
        category, process, tagIds, isActive: true,
      });
      if (res?.success) onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth="md" fullWidth
      slotProps={{ paper: { sx: modalPaperSx } }}
    >
      <ModalHeader
        icon={<NoteAddOutlinedIcon />}
        title={t('documentBank.newDocument', 'Yeni Doküman')}
        subtitle={t('documentBank.newDocumentHint', 'Dosya, atamalar ve notlarla birlikte yayınla')}
        onClose={onClose}
        tone="primary"
      />
      <DialogContent sx={{ p: 3 }}>
        {submitting && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
        <Stack spacing={2.5}>
          <Box>
            <Typography sx={{ ...sectionLabelSx, mb: 1 }}>
              {t('documentBank.sections.details', 'Detaylar')}
            </Typography>
            <Stack spacing={2}>
              <TextField
                label={t('documentBank.fields.title', 'Başlık')}
                value={title} onChange={(e) => setTitle(e.target.value)}
                required autoFocus fullWidth size="small"
              />
              <TextField
                label={t('documentBank.fields.description', 'Açıklama')}
                value={description} onChange={(e) => setDescription(e.target.value)}
                fullWidth multiline rows={2} size="small"
              />
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography sx={{ ...sectionLabelSx, mb: 1 }}>
              {t('documentBank.sections.taxonomy', 'Sınıflandırma')}
            </Typography>
            <TaxonomySection
              category={category} setCategory={setCategory}
              process={process} setProcess={setProcess}
              tagIds={tagIds} setTagIds={setTagIds}
              tags={tags}
            />
          </Box>

          <Divider />

          <Box>
            <Typography sx={{ ...sectionLabelSx, mb: 1 }}>
              {t('documentBank.sections.file', 'Dosya')}
            </Typography>
            <Stack spacing={2}>
              <FileField
                field={{ label: null, required: true }}
                value={file}
                onChange={(_name, v) => setFile(v)}
              />
              <TextField
                label={t('documentBank.fields.notes', 'Versiyon Notu')}
                placeholder={t('documentBank.fields.notesPlaceholder', 'Bu versiyonda ne değişti?')}
                value={notes} onChange={(e) => setNotes(e.target.value)}
                fullWidth multiline rows={2} size="small"
              />
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography sx={{ ...sectionLabelSx, mb: 1 }}>
              {t('documentBank.sections.assignments', 'Atamalar')}
            </Typography>
            <DocumentAssignmentsField
              field={{ label: '' }}
              value={assignments}
              onChange={(_name, v) => setAssignments(v)}
              currentUser={currentUser}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={modalActionsSx}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          {t('common.cancel', 'İptal')}
        </Button>
        <Button
          variant="contained" onClick={handleSubmit} disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
          sx={{ textTransform: 'none', fontWeight: 600, px: 2.5 }}
        >
          {t('documentBank.actions.uploadAndPublish', 'Yükle ve Yayınla')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Custom modal: Meta Duzenle (kategori/surec/etiket/aktif) ─────────
const EditMetaModal = ({ open, doc, onClose, onSubmit, tags }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState(null);
  const [process, setProcess] = useState(null);
  const [tagIds, setTagIds] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !doc) return;
    setTitle(doc.title || '');
    setDescription(doc.description || '');
    setCategory(doc.category || null);
    setProcess(doc.process || null);
    // Enrichment `tags: [{id, name, color}]` olarak flatten ediyor — buradan
    // `id`'leri okuyoruz. (tag_assignments.raw da hala geliyor ama artik
    // duzlestirilmis versiyonu kullanmak tutarli.)
    setTagIds((doc.tags || []).map(t => t.id).filter(v => v != null));
    setIsActive(doc.is_active !== false);
    setSubmitting(false);
  }, [open, doc]);

  const handleSave = async () => {
    if (!doc || !title.trim()) return;
    setSubmitting(true);
    try {
      const res = await onSubmit({
        id: doc.id, title: title.trim(), description,
        category, process, tagIds, isActive,
      });
      if (res?.success) onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth="sm" fullWidth
      slotProps={{ paper: { sx: modalPaperSx } }}
    >
      <ModalHeader
        icon={<EditOutlinedIcon />}
        title={t('documentBank.actions.editMeta', 'Doküman Düzenle')}
        subtitle={doc?.title || ''}
        onClose={onClose}
        tone="primary"
      />
      <DialogContent sx={{ p: 3 }}>
        {submitting && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
        <Stack spacing={2.5}>
          <TextField
            label={t('documentBank.fields.title', 'Başlık')}
            value={title} onChange={(e) => setTitle(e.target.value)}
            required fullWidth size="small"
          />
          <TextField
            label={t('documentBank.fields.description', 'Açıklama')}
            value={description} onChange={(e) => setDescription(e.target.value)}
            fullWidth multiline rows={2} size="small"
          />
          <TaxonomySection
            category={category} setCategory={setCategory}
            process={process} setProcess={setProcess}
            tagIds={tagIds} setTagIds={setTagIds}
            tags={tags}
          />
          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label={isActive
              ? t('documentBank.status.active', 'Aktif')
              : t('documentBank.status.passive', 'Pasif')}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={modalActionsSx}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          {t('common.cancel', 'İptal')}
        </Button>
        <Button
          variant="contained" onClick={handleSave} disabled={submitting || !title.trim()}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
          sx={{ textTransform: 'none', fontWeight: 600, px: 2.5 }}
        >
          {t('common.save', 'Kaydet')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Custom modal: Yeni Versiyon ─────────────────────────────────────
const NewVersionModal = ({ open, doc, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNotes(''); setFile(null); setSubmitting(false);
  }, [open]);

  const handleSubmit = async () => {
    if (!file || !doc) return;
    setSubmitting(true);
    try {
      const res = await onSubmit({ documentId: doc.id, file, notes });
      if (res?.success) onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const latest = doc?.versions?.[0];
  const latestMeta = latest ? getDocumentFileTypeMeta(latest.mime_type || latest.file_name) : null;
  const nextVersion = latest ? (latest.version_number || 0) + 1 : 1;

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth="sm" fullWidth
      slotProps={{ paper: { sx: modalPaperSx } }}
    >
      <ModalHeader
        icon={<DriveFolderUploadIcon />}
        title={t('documentBank.actions.newVersion', 'Yeni Versiyon')}
        subtitle={doc?.title || ''}
        onClose={onClose}
        tone="primary"
        meta={
          <Chip
            label={`v${nextVersion}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 700, letterSpacing: 0.3 }}
          />
        }
      />

      <DialogContent sx={{ p: 3 }}>
        {submitting && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
        <Stack spacing={2.5}>
          {latest && (
            <Box>
              <Typography sx={{ ...sectionLabelSx, mb: 1 }}>
                {t('documentBank.currentVersion', 'Mevcut versiyon')}
              </Typography>
              <Box
                sx={{
                  p: 1.5, borderRadius: 2,
                  border: '1px solid', borderColor: 'divider',
                  display: 'flex', alignItems: 'center', gap: 1.5,
                }}
              >
                {latestMeta && <latestMeta.Icon sx={{ color: latestMeta.color, fontSize: 28 }} />}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {latest.file_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    v{latest.version_number}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          <Box>
            <Typography sx={{ ...sectionLabelSx, mb: 1 }}>
              {t('documentBank.sections.newFile', 'Yeni dosya')}
            </Typography>
            <FileField
              field={{ label: null, required: true }}
              value={file}
              onChange={(_name, v) => setFile(v)}
            />
          </Box>

          <TextField
            label={t('documentBank.fields.notes', 'Versiyon Notu')}
            placeholder={t('documentBank.fields.notesPlaceholder', 'Bu versiyonda ne değişti?')}
            value={notes} onChange={(e) => setNotes(e.target.value)}
            fullWidth multiline rows={3} size="small"
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={modalActionsSx}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          {t('common.cancel', 'İptal')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!file || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
          sx={{ fontWeight: 600, textTransform: 'none', px: 2.5 }}
        >
          {t('documentBank.actions.uploadNewVersion', 'Versiyon Yükle')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Custom modal: Atamalar ──────────────────────────────────────────
const AssignmentsModal = ({ open, doc, onClose, onSubmit, currentUser }) => {
  const { t } = useTranslation();
  const [assignments, setAssignments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const list = (doc?.assignments || []).map(a => ({
      type: a.assignment_type, refId: a.assignment_ref_id,
    }));
    setAssignments(list);
    setSubmitting(false);
  }, [open, doc]);

  const handleSave = async () => {
    if (!doc) return;
    setSubmitting(true);
    try {
      const res = await onSubmit({ documentId: doc.id, assignments });
      if (res?.success) onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth="sm" fullWidth
      slotProps={{ paper: { sx: modalPaperSx } }}
    >
      <ModalHeader
        icon={<GroupsIcon />}
        title={t('documentBank.actions.manageAssignments', 'Atamaları Yönet')}
        subtitle={doc?.title || ''}
        onClose={onClose}
        tone="primary"
        meta={
          <Chip
            size="small"
            variant="outlined"
            label={t('documentBank.assignments.selectedCount', '{{n}} atama', { n: assignments.length })}
            sx={{ fontWeight: 600 }}
          />
        }
      />
      <DialogContent sx={{ p: 3 }}>
        {submitting && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
        <DocumentAssignmentsField
          field={{ label: '' }}
          value={assignments}
          onChange={(_name, v) => setAssignments(v)}
          currentUser={currentUser}
        />
      </DialogContent>
      <DialogActions sx={modalActionsSx}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          {t('common.cancel', 'İptal')}
        </Button>
        <Button
          variant="contained" onClick={handleSave} disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
          sx={{ textTransform: 'none', fontWeight: 600, px: 2.5 }}
        >
          {t('common.save', 'Kaydet')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Versiyonlar (read-only) ──────────────────────────────────────────
const formatVersionDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' })
      + ' · ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const formatBytes = (bytes) => {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const VersionsModal = ({ open, doc, onClose, onDownload }) => {
  const { t } = useTranslation();
  const versions = doc?.versions || [];
  const latestVersion = versions[0]?.version_number;

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth="sm" fullWidth
      slotProps={{ paper: { sx: modalPaperSx } }}
    >
      <ModalHeader
        icon={<HistoryIcon />}
        title={t('documentBank.versionsOf', 'Versiyonlar')}
        subtitle={doc?.title || '-'}
        onClose={onClose}
        tone="primary"
        meta={
          versions.length > 0 && (
            <Chip
              size="small"
              variant="outlined"
              label={t('documentBank.versionsCount', '{{n}} versiyon', { n: versions.length })}
              sx={{ fontWeight: 600 }}
            />
          )
        }
      />
      <DialogContent sx={{ p: 0 }}>
        {versions.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <DescriptionOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary" variant="body2">
              {t('documentBank.noVersions', 'Versiyon yok')}
            </Typography>
          </Box>
        ) : (
          <Stack divider={<Divider />}>
            {versions.map((v) => {
              const meta = getDocumentFileTypeMeta(v.mime_type || v.file_name);
              const isLatest = v.version_number === latestVersion;
              return (
                <Box
                  key={v.id}
                  sx={{
                    px: 3, py: 2,
                    display: 'flex', alignItems: 'center', gap: 2,
                    transition: 'background-color 0.15s',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <meta.Icon sx={{ color: meta.color, fontSize: 32, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                      <Typography variant="body2" fontWeight={700}>
                        v{v.version_number}
                      </Typography>
                      {isLatest && (
                        <Chip
                          label={t('documentBank.latest', 'Güncel')}
                          size="small"
                          color="primary"
                          sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                        />
                      )}
                      <Typography variant="caption" color="text.secondary">
                        · {formatVersionDate(v.uploaded_at || v.created_at)}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" noWrap sx={{ color: 'text.primary' }}>
                      {v.file_name}
                    </Typography>
                    {(v.notes || v.file_size) && (
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                        {v.file_size && (
                          <Typography variant="caption" color="text.disabled">
                            {formatBytes(v.file_size)}
                          </Typography>
                        )}
                        {v.notes && (
                          <Typography
                            variant="caption" color="text.secondary"
                            sx={{ fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            noWrap
                          >
                            “{v.notes}”
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Box>
                  <Tooltip title={t('common.download', 'İndir')}>
                    <IconButton
                      size="small"
                      onClick={() => onDownload(v)}
                      sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={modalActionsSx}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('common.close', 'Kapat')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Sil onay modal ───────────────────────────────────────────────────
const DeleteConfirmModal = ({ open, doc, onClose, onConfirm, submitting }) => {
  const { t } = useTranslation();
  const count = doc?.versions?.length || 0;
  return (
    <Dialog
      open={open} onClose={onClose} maxWidth="xs" fullWidth
      slotProps={{ paper: { sx: modalPaperSx } }}
    >
      <ModalHeader
        icon={<WarningAmberIcon />}
        title={t('documentBank.deleteTitle', 'Doküman silinsin mi?')}
        subtitle={doc?.title || ''}
        onClose={onClose}
        tone="error"
      />
      <DialogContent sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t(
            'documentBank.deleteConfirm.long',
            '"{{title}}" adlı doküman ve tüm versiyonları/atamaları silinecek.',
            { title: doc?.title || '' }
          )}
        </Typography>
        {count > 0 && (
          <Box
            sx={{
              mt: 2, p: 1.5, borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
              border: '1px solid',
              borderColor: (theme) => alpha(theme.palette.error.main, 0.3),
              display: 'flex', alignItems: 'center', gap: 1,
            }}
          >
            <WarningAmberIcon fontSize="small" sx={{ color: 'error.main' }} />
            <Typography variant="caption" sx={{ color: 'error.dark', fontWeight: 600 }}>
              {t('documentBank.deleteImpact', '{{n}} versiyon geri döndürülemez şekilde silinecek', { n: count })}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={modalActionsSx}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          {t('common.cancel', 'İptal')}
        </Button>
        <Button
          color="error" variant="contained" onClick={onConfirm} disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
          sx={{ textTransform: 'none', fontWeight: 600, px: 2.5 }}
        >
          {t('common.delete', 'Sil')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Sayfa ────────────────────────────────────────────────────────────
const DocumentBankPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const canManage = user && MANAGE_ROLES.has(user.role);
  const isRoot = user?.role === USER_ROLES.ROOT;

  const bank = useDocumentBank();
  const [remountKey, setRemountKey] = useState(0);
  const refresh = useCallback(() => setRemountKey(k => k + 1), []);

  // Modallar
  const [uploadModal, setUploadModal] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, doc: null });
  const [versionsModal, setVersionsModal] = useState({ open: false, doc: null });
  const [versionModal, setVersionModal] = useState({ open: false, doc: null });
  const [assignModal, setAssignModal] = useState({ open: false, doc: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, doc: null });
  const [deleting, setDeleting] = useState(false);
  const bankRef = useRef(bank);
  bankRef.current = bank;

  // Backend proxy uzerinden indirme: browser dogrudan S3'e gitmedigi icin
  // kurumsal firewall/antivirus engellerine takilmaz; anchor click ile
  // kaydedildigi icin popup blocker'a da takilmaz.
  const handleDownload = useCallback(async (version) => {
    const key = extractS3Key(version?.file_url);
    if (!key) {
      showNotification(t('documentBank.notifications.noFile', 'Dosya bulunamadı'), 'warning');
      return;
    }
    try {
      const fileName = version?.file_name || key.split('/').pop() || 'download';
      const blob = await s3Service.downloadFileAsBlob(key, fileName);
      if (!blob) {
        showNotification(
          t('documentBank.notifications.downloadError', 'İndirme bağlantısı alınamadı'),
          'error',
        );
        return;
      }
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      showNotification(
        t('documentBank.notifications.downloadError', 'İndirme bağlantısı alınamadı'),
        'error',
      );
    }
  }, [showNotification, t]);

  const canDeleteRow = useCallback((row) => {
    if (!user || !row) return false;
    if (isRoot) return true;
    return row.created_by === user.id;
  }, [user, isRoot]);

  const canEditRow = useCallback((row) => {
    if (!user || !row) return false;
    if (isRoot) return true;
    // DIRECTOR/GROUP_MANAGER/TEAM_MANAGER: kendi sirket dokumanini gunc.
    return canManage && row.company_id === user.companyId;
  }, [user, isRoot, canManage]);

  const customRowActions = useCallback((row) => {
    const actions = [
      {
        type: 'downloadLatest',
        label: t('documentBank.actions.download', 'İndir'),
        color: 'primary',
        icon: <DownloadIcon fontSize="small" />,
      },
      {
        type: 'viewVersions',
        label: t('documentBank.actions.viewVersions', 'Versiyonlar'),
        color: 'info',
        icon: <HistoryIcon fontSize="small" />,
      },
    ];
    if (canEditRow(row)) {
      actions.push({
        type: 'editMeta',
        label: t('documentBank.actions.editMeta', 'Düzenle'),
        color: 'default',
        icon: <EditOutlinedIcon fontSize="small" />,
      });
    }
    if (canManage) {
      actions.push(
        {
          type: 'newVersion',
          label: t('documentBank.actions.newVersion', 'Yeni Versiyon'),
          color: 'secondary',
          icon: <UploadFileIcon fontSize="small" />,
        },
        {
          type: 'manageAssignments',
          label: t('documentBank.actions.manageAssignments', 'Atamalar'),
          color: 'default',
          icon: <GroupsIcon fontSize="small" />,
        },
      );
    }
    if (canEditRow(row)) {
      actions.push({
        type: 'toggleActive',
        label: row.is_active === false
          ? t('documentBank.actions.activate', 'Aktifleştir')
          : t('documentBank.actions.deactivate', 'Pasifleştir'),
        color: row.is_active === false ? 'success' : 'warning',
        icon: row.is_active === false
          ? <ToggleOnIcon fontSize="small" />
          : <ToggleOffIcon fontSize="small" />,
      });
    }
    if (canDeleteRow(row)) {
      actions.push({
        type: 'deleteDoc',
        label: t('documentBank.actions.delete', 'Sil'),
        color: 'error',
        icon: <DeleteOutlineIcon fontSize="small" />,
      });
    }
    return actions;
  }, [canManage, canDeleteRow, canEditRow, t]);

  const handleRowAction = useCallback((type, row) => {
    switch (type) {
      case 'downloadLatest':
        handleDownload(row.versions?.[0]);
        return true;
      case 'viewVersions':
        setVersionsModal({ open: true, doc: row });
        return true;
      case 'editMeta':
        setEditModal({ open: true, doc: row });
        return true;
      case 'newVersion':
        setVersionModal({ open: true, doc: row });
        return true;
      case 'manageAssignments':
        setAssignModal({ open: true, doc: row });
        return true;
      case 'toggleActive':
        bankRef.current.toggleActive({
          id: row.id,
          isActive: row.is_active === false,
        }).then(res => { if (res?.success) refresh(); });
        return true;
      case 'deleteDoc':
        setDeleteModal({ open: true, doc: row });
        return true;
      default:
        return false;
    }
  }, [handleDownload, refresh]);

  // Custom modal action'lari + refresh wrapper'lari
  const handleCreate = useCallback(async (data) => {
    const res = await bankRef.current.createDocument(data);
    if (res?.success) refresh();
    return res;
  }, [refresh]);

  const handleEditMeta = useCallback(async (data) => {
    const metaRes = await bankRef.current.updateDocumentMeta(data);
    if (!metaRes?.success) return metaRes;
    // Etiketler ayri mutation ile guncelleniyor — meta update sonrasi.
    const tagRes = await bankRef.current.replaceTagAssignments({
      documentId: data.id, tagIds: data.tagIds || [],
    });
    if (tagRes?.success) refresh();
    return tagRes;
  }, [refresh]);

  const handleNewVersion = useCallback(async (data) => {
    const res = await bankRef.current.uploadNewVersion(data);
    if (res?.success) refresh();
    return res;
  }, [refresh]);

  const handleReplaceAssignments = useCallback(async (data) => {
    const res = await bankRef.current.replaceAssignments(data);
    if (res?.success) refresh();
    return res;
  }, [refresh]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.doc) return;
    setDeleting(true);
    try {
      const res = await bankRef.current.deleteDocumentBank(deleteModal.doc.id);
      if (res?.success) {
        setDeleteModal({ open: false, doc: null });
        refresh();
      }
    } finally {
      setDeleting(false);
    }
  }, [deleteModal.doc, refresh]);

  const headerExtra = canManage ? (
    <Button
      variant="contained" startIcon={<AddIcon />}
      onClick={() => setUploadModal(true)}
      sx={{ fontWeight: 600, textTransform: 'none' }}
    >
      {t('documentBank.newDocument', 'Yeni Doküman')}
    </Button>
  ) : null;

  // DynamicCrudPage'e customFilterConfigs verildiginde standart else branch
  // atlanır (showIfRoot / status labelKey→label / vs). Bu yuzden onlari
  // burada elle yapiyoruz — OffersPage/ContractsPage pattern'i ayni sekilde.
  //   * 'tags' → sirket dokuman etiketleri options'i doldur (job etiketleri
  //     yerine kendi CRUD tablosundan). Etiket id + isim map.
  //   * 'status' → labelKey degerlerini t() ile ceviriye tabi tut.
  //   * showIfRoot true olan filter'lari ROOT disinda gizle.
  const customFilterConfigs = useCallback((baseFilters, _addData, isRootUser) => {
    // Etiket options'i iki kaynaktan besle: (1) sirket etiket tablosundan
    // (bank.tags), (2) fallback olarak data'da halihazirda kullanilan
    // tag object'lerinden. Ikinci kaynak, Hasura metadata henuz apply
    // edilmemis (bank.tags fetch bos donmus) durumu da toparlar.
    const bySource = new Map();
    (bank.tags || []).forEach(tg => bySource.set(tg.id, tg));
    (bank.documentBank || []).forEach(d => {
      (d.tags || []).forEach(tg => {
        if (tg?.id != null && !bySource.has(tg.id)) bySource.set(tg.id, tg);
      });
    });
    const tagOptions = Array.from(bySource.values())
      .map(tg => ({ value: tg.id, label: tg.name || `#${tg.id}` }));

    return baseFilters
      .filter(f => (f.showIfRoot ? isRootUser : true))
      .map(f => {
        if (f.field === 'docTagIds') {
          return { ...f, options: tagOptions };
        }
        if (f.field === 'status' && Array.isArray(f.options)) {
          return {
            ...f,
            options: f.options.map(opt => ({
              value: opt.value,
              label: opt.labelKey ? t(opt.labelKey) : opt.label,
            })),
          };
        }
        return f;
      });
  }, [bank.tags, bank.documentBank, t]);

  return (
    <>
      <DynamicCrudPage
        key={remountKey}
        entityType="documentBank"
        hook={useDocumentBank}
        screen={null}
        showAddButton={false}
        headerExtraContent={headerExtra}
        customRowActions={customRowActions}
        onRowAction={handleRowAction}
        customFilterConfigs={customFilterConfigs}
        hideDefaultActions={true}
      />

      <UploadModal
        open={uploadModal}
        onClose={() => setUploadModal(false)}
        onSubmit={handleCreate}
        currentUser={user}
        tags={bank.tags}
      />

      <EditMetaModal
        open={editModal.open}
        doc={editModal.doc}
        onClose={() => setEditModal({ open: false, doc: null })}
        onSubmit={handleEditMeta}
        tags={bank.tags}
      />

      <NewVersionModal
        open={versionModal.open}
        doc={versionModal.doc}
        onClose={() => setVersionModal({ open: false, doc: null })}
        onSubmit={handleNewVersion}
      />

      <AssignmentsModal
        open={assignModal.open}
        doc={assignModal.doc}
        onClose={() => setAssignModal({ open: false, doc: null })}
        onSubmit={handleReplaceAssignments}
        currentUser={user}
      />

      <VersionsModal
        open={versionsModal.open}
        doc={versionsModal.doc}
        onClose={() => setVersionsModal({ open: false, doc: null })}
        onDownload={handleDownload}
      />

      <DeleteConfirmModal
        open={deleteModal.open}
        doc={deleteModal.doc}
        onClose={() => setDeleteModal({ open: false, doc: null })}
        onConfirm={handleDeleteConfirm}
        submitting={deleting}
      />
    </>
  );
};

export default DocumentBankPage;
